import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [4.711, -74.0721];
const DEFAULT_ZOOM = 5;
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

function formatCoord(value) {
  if (!Number.isFinite(value)) return '';
  return String(value);
}

function parseCoord(value) {
  const trimmed = String(value ?? '').trim().replace(',', '.');
  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function isValidLat(value) {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

function isValidLon(value) {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

/**
 * Traduce el `boundingbox` de un resultado de Nominatim a un radio aproximado en
 * metros. Un resultado de tipo "edificio" devuelve una caja de decenas de metros
 * y uno de tipo "ciudad" de kilómetros: guardar esa diferencia es lo que permite
 * distinguir después una coordenada fina de una que solo apunta al municipio.
 * Devuelve null si el resultado no trae caja utilizable.
 */
function precisionFromBoundingBox(boundingbox, lat) {
  if (!Array.isArray(boundingbox) || boundingbox.length < 4) return null;
  const [south, north, west, east] = boundingbox.map(Number);
  if (![south, north, west, east].every(Number.isFinite)) return null;
  const latMeters = Math.abs(north - south) * 111320;
  const lonMeters =
    Math.abs(east - west) * 111320 * Math.cos((lat * Math.PI) / 180);
  const radius = Math.max(latMeters, lonMeters) / 2;
  if (!Number.isFinite(radius) || radius <= 0) return null;
  // Nominatim no da precisión sub-decamétrica de forma fiable.
  return Math.max(10, Math.round(radius));
}

function MapClickHandler({ onPick, enabled }) {
  useMapEvents({
    click(event) {
      if (!enabled) return;
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function MapViewSync({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center || center[0] == null || center[1] == null) return;
    map.setView(center, zoom ?? map.getZoom());
  }, [center, zoom, map]);
  return null;
}

function DraggableMarker({ position, onDragEnd, draggable }) {
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (!marker) return;
        const { lat, lng } = marker.getLatLng();
        onDragEnd(lat, lng);
      },
    }),
    [onDragEnd]
  );

  if (!position) return null;

  return (
    <Marker
      draggable={Boolean(draggable)}
      eventHandlers={draggable ? eventHandlers : undefined}
      position={position}
      ref={markerRef}
    />
  );
}

function IeLocationMap({ lat, lon, onChange, readOnly = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [latInput, setLatInput] = useState(() => formatCoord(lat));
  const [lonInput, setLonInput] = useState(() => formatCoord(lon));
  const debounceRef = useRef(null);
  const editingFieldRef = useRef(null);

  const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
  const markerPos = hasCoords ? [lat, lon] : null;
  const mapCenter = flyTarget?.center || markerPos || DEFAULT_CENTER;
  const mapZoom = flyTarget?.zoom ?? (hasCoords ? 15 : DEFAULT_ZOOM);

  // Cada cambio de coordenada declara de dónde viene: el buscador de lugar marca
  // NOMINATIM (con la precisión que se deduce de su boundingbox) y el pin puesto
  // a mano marca MANUAL. Así una coordenada corregida a mano deja de figurar como
  // si siguiera viniendo de la fuente oficial.
  const emitChange = useCallback(
    (nextLat, nextLon, source = 'MANUAL', precisionM = null) => {
      if (readOnly) return;
      if (typeof onChange === 'function') {
        onChange({ lat: nextLat, lon: nextLon, source, precisionM });
      }
    },
    [onChange, readOnly]
  );

  useEffect(() => {
    if (editingFieldRef.current === 'lat') return;
    setLatInput(formatCoord(lat));
  }, [lat]);

  useEffect(() => {
    if (editingFieldRef.current === 'lon') return;
    setLonInput(formatCoord(lon));
  }, [lon]);

  useEffect(() => {
    if (readOnly || !query.trim()) {
      setResults([]);
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({
          q: query.trim(),
          format: 'json',
          addressdetails: '0',
          limit: '6',
        });
        const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          setResults([]);
          return;
        }
        const data = await response.json();
        setResults(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error buscando en Nominatim:', error);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, readOnly]);

  const applyCoordinates = (
    nextLat,
    nextLon,
    { fly = true, zoom = 15, source = 'MANUAL', precisionM = null } = {}
  ) => {
    if (!isValidLat(nextLat) || !isValidLon(nextLon)) return;
    if (fly) {
      setFlyTarget({ center: [nextLat, nextLon], zoom });
    } else {
      setFlyTarget(null);
    }
    setLatInput(formatCoord(nextLat));
    setLonInput(formatCoord(nextLon));
    emitChange(nextLat, nextLon, source, precisionM);
  };

  const handleSelectResult = (item) => {
    if (readOnly) return;
    const nextLat = parseFloat(item.lat);
    const nextLon = parseFloat(item.lon);
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLon)) return;
    editingFieldRef.current = null;
    applyCoordinates(nextLat, nextLon, {
      fly: true,
      zoom: 16,
      source: 'NOMINATIM',
      precisionM: precisionFromBoundingBox(item.boundingbox, nextLat),
    });
    setQuery(item.display_name || '');
    setResults([]);
  };

  const handlePick = (nextLat, nextLon) => {
    if (readOnly) return;
    editingFieldRef.current = null;
    applyCoordinates(nextLat, nextLon, { fly: false });
  };

  const tryApplyFromInputs = (nextLatText, nextLonText) => {
    const nextLat = parseCoord(nextLatText);
    const nextLon = parseCoord(nextLonText);
    if (!isValidLat(nextLat) || !isValidLon(nextLon)) return;
    applyCoordinates(nextLat, nextLon, { fly: true, zoom: hasCoords ? 15 : 12 });
  };

  const handleLatChange = (event) => {
    const value = event.target.value;
    setLatInput(value);
    tryApplyFromInputs(value, lonInput);
  };

  const handleLonChange = (event) => {
    const value = event.target.value;
    setLonInput(value);
    tryApplyFromInputs(latInput, value);
  };

  const handleCoordBlur = (field) => {
    editingFieldRef.current = null;
    const nextLat = parseCoord(latInput);
    const nextLon = parseCoord(lonInput);
    if (isValidLat(nextLat) && isValidLon(nextLon)) {
      applyCoordinates(nextLat, nextLon, { fly: true, zoom: hasCoords ? 15 : 12 });
      return;
    }
    if (field === 'lat') {
      setLatInput(formatCoord(lat));
    } else {
      setLonInput(formatCoord(lon));
    }
  };

  return (
    <div className="ie-location-map">
      {!readOnly && (
        <div className="ie-location-map__search mb-2">
          <label className="form-label mb-1">Buscar lugar</label>
          <input
            type="search"
            className="form-control"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ej: Universidad Nacional, Bogotá"
            autoComplete="off"
          />
          {searching && <div className="form-text">Buscando…</div>}
          {results.length > 0 && (
            <ul className="ie-location-map__results list-group mt-1">
              {results.map((item) => (
                <button
                  key={`${item.place_id}-${item.osm_id}`}
                  type="button"
                  className="list-group-item list-group-item-action text-start"
                  onClick={() => handleSelectResult(item)}
                >
                  {item.display_name}
                </button>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="ie-location-map__coords row g-2 mb-2">
        <div className="col-6">
          <label className="form-label mb-1">Latitud</label>
          <input
            type="text"
            inputMode="decimal"
            className="form-control"
            value={latInput}
            onChange={handleLatChange}
            onFocus={() => {
              editingFieldRef.current = 'lat';
            }}
            onBlur={() => handleCoordBlur('lat')}
            placeholder="Ej: 4.711000"
            disabled={readOnly}
            readOnly={readOnly}
            autoComplete="off"
          />
        </div>
        <div className="col-6">
          <label className="form-label mb-1">Longitud</label>
          <input
            type="text"
            inputMode="decimal"
            className="form-control"
            value={lonInput}
            onChange={handleLonChange}
            onFocus={() => {
              editingFieldRef.current = 'lon';
            }}
            onBlur={() => handleCoordBlur('lon')}
            placeholder="Ej: -74.072100"
            disabled={readOnly}
            readOnly={readOnly}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="ie-location-map__canvas">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewSync center={flyTarget?.center || null} zoom={flyTarget?.zoom} />
          <MapClickHandler onPick={handlePick} enabled={!readOnly} />
          <DraggableMarker
            position={markerPos}
            onDragEnd={handlePick}
            draggable={!readOnly}
          />
        </MapContainer>
      </div>
      <div className="form-text mt-2">
        {readOnly
          ? 'Ubicación de la institución.'
          : 'Escribe lat/lon, haz click en el mapa o arrastra el pin.'}
      </div>
    </div>
  );
}

export default IeLocationMap;
